import { auth, toasts } from '../api.js'
import { changesHref, issueHref, linkify } from '../autolinks.js'
import { claudeCliUrl, claudeTask, claudeVscodeUrl } from '../claude.js'
import { icon, stateIconView } from '../icons.js'
import { DONE } from '../lanes.js'
import m from '../m.js'
import { settings } from '../settings.js'
import { board } from '../store.js'
import {
  asyncButtonView,
  avatarView,
  dropOpen,
  labelView,
  labelDotView,
  pickerView,
} from './widgets.js'

const CHECK_ICONS = {
  SUCCESS: 'check-circle-fill',
  FAILURE: 'x-circle-fill',
  ERROR: 'x-circle-fill',
}
const MERGE_STATES = ['BEHIND', 'DIRTY', 'UNKNOWN']

/** Why a PR's checks are not green, or null; unless waiting for them it can be merged anyway. */
function checksBlocker(prStatus) {
  if (prStatus.checks && prStatus.checks !== 'SUCCESS')
    return m.t(`checks.${prStatus.checks}`)
  return prStatus.mergeState === 'UNSTABLE'
    ? m.t('merge.blocked.UNSTABLE')
    : null
}

/** Why a PR cannot be merged from the board, not counting checks GitHub does not require. */
export function mergeBlocker(card, repo) {
  const { draft, status: prStatus } = card.pr
  if (!repo.canWrite) return m.t('merge.blocked.noWrite')
  if (!repo.mergeMethods.length) return m.t('merge.blocked.disabled')
  if (draft || prStatus?.mergeState === 'DRAFT')
    return m.t('merge.blocked.draft')
  if (!prStatus) return m.t('merge.blocked.loading')
  if (prStatus.review === 'CHANGES_REQUESTED')
    return m.t('review.CHANGES_REQUESTED')
  // branch protection: GitHub refuses the merge, failing checks are the likeliest reason
  if (prStatus.mergeState === 'BLOCKED')
    return (
      checksBlocker(prStatus) ??
      m.t(
        prStatus.review === 'REVIEW_REQUIRED'
          ? 'review.REVIEW_REQUIRED'
          : 'merge.blocked.BLOCKED'
      )
    )
  return MERGE_STATES.includes(prStatus.mergeState)
    ? m.t(`merge.blocked.${prStatus.mergeState}`)
    : null
}

// octicons has no squash or rebase icon: squash presses commits together,
// rebase leaves a straight line of commits
const MERGE_ICONS = { merge: 'git-merge', squash: 'fold', rebase: 'git-commit' }

// one commit: a merge commit or a squash only add noise, rebase keeps it as it is
function mergeMethods(card, repo) {
  return card.pr.commits === 1 && repo.mergeMethods.includes('rebase')
    ? ['rebase']
    : repo.mergeMethods
}

/** "Delete branch <name>" with the name styled; the translation decides where it goes. */
function deleteBranchLabel(branch) {
  const [before, after] = m
    .t('merge.deleteBranch', { branch: '\u0000' })
    .split('\u0000')
  return [before, m('code.branch-name', branch), after]
}

/**
 * Two-click merge: the button opens the repo's allowed methods, picking one merges.
 * `compact`: icon only (cards); the reason a merge is blocked is in the tooltip.
 * `onmerged`: called after a successful merge (the detail dialog closes).
 */
export const mergeButtonView = m.withState(() => {
  let busy = false
  let deleteBranch = true
  let waitForChecks = true
  async function merge(card, method, deleteAfter, onmerged) {
    busy = true
    let merged = false
    try {
      await board.merge(card.number, method, deleteAfter)
      merged = true
    } catch {
      // the store shows the error as a toast
    } finally {
      busy = false
    }
    if (merged) onmerged?.()
  }
  return ({ card, repo, compact, onmerged }) => {
    const blocker = mergeBlocker(card, repo)
    const checks = blocker ? null : checksBlocker(card.pr.status)
    const waiting = checks && waitForChecks
    const ready = !blocker && !checks && !busy
    // GitHub deletes it itself when the repo says so; branches of forks are not ours
    const canDelete = !repo.deleteBranchOnMerge && !card.pr.crossRepo
    return dropOpen({
      tagName: '.merge',
      align: 'left',
      menuClass: 'merge-menu',
      // the checkbox keeps it open, picking a method closes it
      closeOnContentClick: false,
      onClick: evt => evt.stopPropagation(),
      ondragstart: evt => evt.preventDefault(),
      handleView: (isOpen, anchor) =>
        m.m(
          'button.merge-button',
          {
            ...anchor,
            cls: { 'icon-button': compact, primary: ready },
            title: checks || m.t('merge.choose'),
            'aria-label': busy
              ? m.t('merge.busy')
              : blocker || checks || m.t('merge.choose'),
            disabledReason: [
              [busy, m.t('merge.busy')],
              [blocker, blocker],
            ],
          },
          compact
            ? icon('git-merge')
            : busy
              ? m.t('merge.busy')
              : blocker || [
                  icon('git-merge'),
                  checks || m.t('merge.button'),
                  icon('triangle-down'),
                ]
        ),
      contentView: closeMenu => [
        mergeMethods(card, repo).map(method =>
          m.m(
            'button.picker-option',
            {
              title: m.t(`merge.methodHint.${method}`),
              disabledReason: [[waiting, checks]],
              onclick: () => {
                closeMenu()
                merge(card, method, canDelete && deleteBranch, onmerged)
              },
            },
            [icon(MERGE_ICONS[method]), m.t(`merge.method.${method}`)]
          )
        ),
        checks &&
          m('label.picker-footer.merge-wait', [
            m('input[type=checkbox]', {
              checked: waitForChecks,
              onchange: evt => (waitForChecks = evt.target.checked),
            }),
            m.t('merge.waitForChecks'),
          ]),
        canDelete &&
          m('label.picker-footer.merge-delete', [
            m('input[type=checkbox]', {
              checked: deleteBranch,
              onchange: evt => (deleteBranch = evt.target.checked),
            }),
            deleteBranchLabel(card.pr.branch),
          ]),
        // unknown on boards saved before the count was loaded
        card.pr.commits != null &&
          m(
            '.picker-footer.merge-commits',
            m.t('merge.commits', card.pr.commits, {
              branch: card.pr.branch,
              base: card.pr.base,
            })
          ),
      ],
    })
  }
})

export function deleteBranchButtonView({ card, compact }) {
  const hint = m.t('branch.deleteHint', { branch: card.pr.branch })
  return asyncButtonView(
    {
      className: 'delete-branch',
      cls: { 'icon-button': compact },
      title: hint,
      'aria-label': hint,
      busyReason: m.t('branch.deleting'),
      onclick: evt => {
        evt.stopPropagation()
        return board.deleteBranch(card.number)
      },
    },
    [icon('trash'), !compact && m.t('branch.delete')],
    [icon('trash'), !compact && m.t('branch.deleting')]
  )
}

/** The item on GitHub; pull requests open at their changes. */
export function githubHref(card) {
  return card.type === 'pr' ? `${card.url}/changes` : card.url
}

// GitHub Pull Requests extension: checks out the PR, opening or cloning the repo if needed
function vscodeCheckout(card) {
  return `vscode://github.vscode-pull-request-github/checkout-pull-request?uri=${encodeURIComponent(card.url)}`
}

/**
 * "Check out in VS Code" and "Copy branch name"; compact icons on cards, labeled in the
 * modal. `vscode` / `copy`: which of the two to show (cards follow the user's settings).
 */
export const prActionsView = m.withState(() => {
  let copied = false
  async function copy(evt, branch) {
    evt.stopPropagation()
    try {
      await navigator.clipboard.writeText(branch)
      copied = true
      setTimeout(() => {
        copied = false
        m.redraw()
      }, 1500)
    } catch (err) {
      toasts.show(m.t('pr.copyFailed', { error: err.message }))
    }
    m.redraw()
  }
  return ({ card, labeled, vscode = true, copy: showCopy = true }) => {
    const copyHint = copied
      ? m.t('pr.copied')
      : m.t('pr.copyBranchHint', { branch: card.pr.branch })
    const variant = { small: labeled, 'icon-button': !labeled }
    return m.m('.pr-actions', { cls: { labeled } }, [
      vscode &&
        m.m(
          'a.button',
          {
            cls: variant,
            href: vscodeCheckout(card),
            title: m.t('pr.vscodeHint'),
            'aria-label': m.t('pr.vscodeHint'),
          },
          [icon('vscode'), labeled && m.t('pr.vscode')]
        ),
      showCopy &&
        m.m(
          'button',
          {
            cls: variant,
            title: copyHint,
            'aria-label': copyHint,
            onclick: evt => copy(evt, card.pr.branch),
          },
          [
            icon(copied ? 'check' : 'copy'),
            labeled && (copied ? m.t('pr.copied') : m.t('pr.copyBranch')),
          ]
        ),
    ])
  }
})

/**
 * "Work on it with Claude Code" in the terminal and in VS Code, each shown when turned on
 * in the settings; compact icons on cards, labeled in the modal. `repo`: "owner/name".
 * Starting it on an issue moves the issue to the configured lane (board.startClaude).
 */
export function claudeActionsView({ card, repo, labeled }) {
  const task = claudeTask(card)
  const links = [
    settings.claudeCli && {
      href: claudeCliUrl(card, repo),
      icon: 'terminal',
      label: m.t('claude.cli'),
      hint: m.t('claude.cliHint', task, { number: card.number }),
    },
    settings.claudeVscode && {
      href: claudeVscodeUrl(card, repo),
      icon: 'sparkle',
      label: m.t('claude.vscode'),
      hint: m.t('claude.vscodeHint', task, { number: card.number }),
    },
  ].filter(Boolean)
  if (!links.length) return null
  const variant = { small: labeled, 'icon-button': !labeled }
  return m.m(
    '.pr-actions.claude-actions',
    { cls: { labeled } },
    links.map(link =>
      m.m(
        'a.button',
        {
          cls: variant,
          href: link.href,
          title: link.hint,
          'aria-label': link.hint,
          onclick: () => board.startClaude(card.number),
        },
        [icon(link.icon), labeled && link.label]
      )
    )
  )
}

/** GitHub reports the base moved on and the branch rebases onto it without conflicts. */
export function canRebase(card, repo) {
  return (
    repo.canWrite &&
    card.state === 'open' &&
    Boolean(card.pr.status?.rebaseable)
  )
}

export function rebaseButtonView({ card, compact }) {
  const hint = m.t('rebase.hint', {
    branch: card.pr.branch,
    base: card.pr.base,
  })
  return asyncButtonView(
    {
      className: 'rebase',
      cls: { 'icon-button': compact },
      title: hint,
      'aria-label': hint,
      busyReason: m.t('rebase.busy'),
      onclick: evt => {
        evt.stopPropagation()
        return board.rebase(card.number).catch(() => {})
      },
    },
    [icon('sync'), !compact && m.t('rebase.button')],
    [icon('sync'), !compact && m.t('rebase.busy')]
  )
}

export function canDeleteBranch(card, repo) {
  return (
    card.state === 'merged' &&
    card.pr.branchExists &&
    !card.pr.crossRepo &&
    repo.canWrite
  )
}

// the title in its own element, so it can end in an ellipsis on narrow lanes
function milestoneView(card) {
  return (
    card.milestone &&
    m('span.milestone', { title: card.milestone.title }, [
      icon('milestone'),
      m('span.milestone-title', card.milestone.title),
    ])
  )
}
function avatars(card) {
  return (
    card.assignees.length > 0 &&
    m(
      '.assignees',
      card.assignees.map(user => avatarView({ user }))
    )
  )
}

// Menus on the card itself; clicks inside must not open the card
function onCard(picker) {
  return m(
    '.card-picker',
    {
      onclick: evt => evt.stopPropagation(),
      ondragstart: evt => evt.preventDefault(),
    },
    picker
  )
}

function cardMilestone(card, data) {
  if (!data.repo.canTriage || card.creating || data.milestones.length === 0)
    return milestoneView(card)
  return onCard(
    pickerView({
      className: card.milestone ? '' : 'empty',
      multi: false,
      title: m.t('card.setMilestone'),
      heading: m.t('card.setMilestone'),
      selected: card.milestone ? [card.milestone.number] : [],
      options: data.milestones.map(ms => ({
        value: ms.number,
        label: ms.title,
        view: () => [icon('milestone'), ms.title],
      })),
      onchange: ([number]) =>
        board.patch(card.number, { milestone: number ?? null }).catch(() => {}),
      toggle: card.milestone
        ? milestoneView(card)
        : m('span.add', icon('milestone')),
    })
  )
}

function cardLabels(card, data) {
  const own = card.labels
  const pills =
    own.length > 0 &&
    m(
      '.labels',
      own.map(label => labelView({ label }))
    )
  if (!data.repo.canTriage || card.creating) return pills
  return onCard(
    pickerView({
      className: own.length ? 'labels-picker' : 'empty',
      // pills flow in the card row (display: contents), which has no box to anchor to
      anchorName: own.length ? `--labels-${card.number}` : undefined,
      align: own.length ? 'left' : 'right',
      title: m.t('card.applyLabels'),
      heading: m.t('card.applyLabels'),
      selected: own.map(l => l.name),
      options: data.labels.map(l => ({
        value: l.name,
        label: l.name,
        view: () => [labelDotView({ color: l.color }), l.name],
      })),
      // lane labels never reach the board; the server keeps them
      onchange: picked =>
        board.patch(card.number, { labels: picked }).catch(() => {}),
      toggle: pills || m('span.add', icon('tag')),
    })
  )
}

function cardAssignees(card, data) {
  if (!data.repo.canTriage || card.creating) return avatars(card)
  const me = auth.user?.login
  // current user first, like GitHub
  const people = [...data.assignees].sort(
    (a, b) => (b.login === me) - (a.login === me)
  )
  return onCard(
    pickerView({
      className: card.assignees.length ? '' : 'empty',
      title: card.assignees.length
        ? card.assignees.map(a => a.login).join(', ')
        : m.t('card.assign'),
      heading: m.t('card.assignHeading'),
      selected: card.assignees.map(a => a.login),
      options: people.map(a => ({
        value: a.login,
        label: a.login,
        view: () => [avatarView({ user: a }), a.login],
      })),
      onchange: assignees =>
        board.patch(card.number, { assignees }).catch(() => {}),
      toggle: avatars(card) || m('span.add', icon('person-add')),
    })
  )
}

/**
 * Claude Code while open; for PRs checkout/copy and merge while open, delete branch once
 * merged.
 */
function cardTools(card, repo) {
  if (card.creating) return []
  const isOpen = card.state === 'open'
  const claude = isOpen && claudeActionsView({ card, repo: repo.fullName })
  if (!card.pr) return claude ? [claude] : []
  if (!isOpen)
    return canDeleteBranch(card, repo)
      ? [deleteBranchButtonView({ card, compact: true })]
      : []
  return [
    claude,
    canRebase(card, repo) && rebaseButtonView({ card, compact: true }),
    (settings.cardVscode || settings.cardCopyBranch) &&
      prActionsView({
        card,
        vscode: settings.cardVscode,
        copy: settings.cardCopyBranch,
      }),
    mergeButtonView({ card, repo, compact: true }),
  ].filter(Boolean)
}

/** Drag start: remember the card, its size and where it was grabbed (see laneView). */
function startDrag(evt, card, lane, dnd) {
  evt.dataTransfer.setData('text/plain', `#${card.number}`)
  evt.dataTransfer.effectAllowed = 'move'
  evt.redraw = false
  const box = evt.currentTarget.getBoundingClientRect()
  dnd.grab = { x: evt.clientX - box.left, y: evt.clientY - box.top }
  dnd.height = box.height
  dnd.number = card.number
  // the card leaves its lane while dragged: the placeholder takes its spot
  const next = evt.currentTarget.nextElementSibling
  dnd.lane = lane
  dnd.before = next?.matches('.card') ? Number(next.dataset.number) : null
  // a tick later: the browser takes the drag image from the card before it is hidden
  setTimeout(m.redraw)
}

/** State, number with GitHub link, checks and comments; the pickers on the right. */
function cardHead(card, data, ownLabels) {
  const checks = card.pr?.status?.checks
  return m('.card-head', [
    stateIconView({ card }),
    m(
      'span.number',
      card.creating
        ? m.t('card.creating')
        : [
            `#${card.number}`,
            m(
              'a.gh-ref',
              {
                href: githubHref(card),
                target: '_blank',
                title: m.t('card.openOnGithub'),
                'aria-label': m.t('card.openOnGithub'),
              },
              icon('mark-github')
            ),
          ]
    ),
    checks &&
      icon(CHECK_ICONS[checks] || 'dot-fill', {
        class: `checks ${checks.toLowerCase()}`,
        title: m.t(`checks.${checks}`),
      }),
    card.comments > 0 &&
      m('span.comments', { title: m.t('card.comments', card.comments) }, [
        icon('comment'),
        card.comments,
      ]),
    m('.spacer'),
    settings.cardLabels && ownLabels.length === 0 && cardLabels(card, data),
    cardMilestone(card, data),
    cardAssignees(card, data),
  ])
}

/** Review state and the linked issues or pull requests. */
function cardFoot(card) {
  const review = card.pr?.status?.review
  const isPr = card.type === 'pr'
  return m('.card-foot', [
    review &&
      m(
        'span.review',
        { class: review.toLowerCase() },
        m.t(`review.${review}`)
      ),
    settings.cardLinks &&
      card.links.length > 0 &&
      m(
        'span.links',
        { title: isPr ? m.t('card.closes') : m.t('card.linkedPulls') },
        [
          icon(isPr ? 'issue-opened' : 'git-pull-request'),
          card.links.map(n =>
            m(
              'a.ref',
              { href: issueHref(n), title: m.t('card.open', { number: n }) },
              `#${n}`
            )
          ),
        ]
      ),
  ])
}

export function cardView({ card, data, lane, dnd }) {
  const { repo } = data
  // hidden on cards when the user turned them off
  const own = settings.cardLabels ? card.labels : []
  const tools = cardTools(card, repo)
  return m.m(
    'article.card',
    {
      key: card.number,
      'data-number': card.number,
      cls: { dragging: dnd.number === card.number, creating: card.creating },
      draggable:
        repo.canTriage &&
        !card.creating &&
        !(card.type === 'pr' && lane === DONE),
      // links on the card (references, GitHub, VS Code) go where they point instead;
      // pull requests open on their changes
      onclick: evt => {
        if (!card.creating && !evt.target.closest('a'))
          m.route.set(
            card.type === 'pr'
              ? changesHref(card.number)
              : issueHref(card.number)
          )
      },
      ondragstart: evt => startDrag(evt, card, lane, dnd),
      ondragend: () => dnd.reset(),
    },
    [
      cardHead(card, data, own),
      m('.card-title', linkify(card.title)),
      // labels, then the PR buttons pushed right (wrapping below when the labels fill the line)
      (own.length > 0 || tools.length > 0) &&
        m.m(
          '.card-row',
          { vars: { 'anchor-name': `--labels-${card.number}` } },
          [
            own.length > 0 && cardLabels(card, data),
            tools.length > 0 && m('.card-tools', tools),
          ]
        ),
      cardFoot(card),
    ]
  )
}
